import type { ScrollGuardStore } from "@ext/agent/components/hooks/scrollGuardStore";
import type { ChatScroller } from "@ext/agent/components/hooks/useChatScroller";
import { type RefObject, useLayoutEffect, useRef } from "react";

type LastTurn = {
	sessionId: string | null;
	userMessageId: string;
};

export const useStickToBottom = (
	scroller: ChatScroller,
	innerContentRef: RefObject<HTMLDivElement>,
	scrollGuardStore: ScrollGuardStore,
	sessionId: string | null,
	lastUserMessageId: string | null,
) => {
	const lastTurnRef = useRef<LastTurn | null>(null);

	useLayoutEffect(() => {
		if (!lastUserMessageId) {
			lastTurnRef.current = null;
			return;
		}

		const prev = lastTurnRef.current;
		lastTurnRef.current = { sessionId, userMessageId: lastUserMessageId };
		if (prev?.sessionId === sessionId && prev.userMessageId !== lastUserMessageId) scroller.scrollToEnd("smooth");

		const inner = innerContentRef.current;
		if (!inner) return;

		let alive = true;
		let rafId: number | null = null;
		const shouldFollow = () => alive && !scroller.isUserScrolledUp.current && !scrollGuardStore.hasOpenGuard();

		const mo = new MutationObserver(() => {
			if (!shouldFollow() || rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				rafId = null;
				if (shouldFollow()) scroller.scrollToEnd("instant");
			});
		});
		mo.observe(inner, { childList: true, subtree: true, characterData: true });

		return () => {
			alive = false;
			if (rafId !== null) cancelAnimationFrame(rafId);
			mo.disconnect();
		};
	}, [sessionId, lastUserMessageId, scroller, innerContentRef, scrollGuardStore]);
};
